import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { EmojiEvents, Map, QueryStats, AutoAwesome, Campaign, SportsEsports } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useReferentialContext } from "../../Context";
import { getNeonColor } from "../../theme";
import { NeonAppBar } from "../../NeonComponents/NeonAppBar";
import TeamIcon from "../Icons/TeamIcon";
import TopMenu from "../../farsight_app_suite/src/layout/TopMenu";
import { BaseUser } from "../../farsight_app_suite/src/types";
import { styled } from "@mui/system";

// Styled Menu Container with neon aesthetic
const MenuContainer = styled(Box)(({ theme }) => ({
    width: "720px",
    background: "rgba(5, 5, 15, 0.98)",
    borderRadius: "16px",
    border: `1px solid ${getNeonColor(theme)}25`,
    boxShadow: `
        0px 25px 80px rgba(0, 0, 0, 0.8),
        0px 0px 40px ${getNeonColor(theme)}15,
        inset 0px 1px 0px rgba(255, 255, 255, 0.05)
    `,
    backdropFilter: "blur(20px)",
    overflow: "hidden",
    position: "relative",
    padding: "24px",
    "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "1px",
        background: `linear-gradient(90deg, 
            transparent 0%, 
            ${getNeonColor(theme)}60 30%,
            ${getNeonColor(theme)} 50%,
            ${getNeonColor(theme)}60 70%,
            transparent 100%)`,
        boxShadow: `0px 0px 20px ${getNeonColor(theme)}`,
    },
}));

// Section Column
const MenuSection = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: "150px",
}));

// Section Header with neon styling
const SectionHeader = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "16px",
    paddingBottom: "12px",
    borderBottom: `1px solid ${getNeonColor(theme)}20`,
    position: "relative",
    "&::after": {
        content: '""',
        position: "absolute",
        bottom: "-1px",
        left: 0,
        width: "40px",
        height: "2px",
        background: getNeonColor(theme),
        boxShadow: `0px 0px 10px ${getNeonColor(theme)}`,
        borderRadius: "2px",
    },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    color: getNeonColor(theme),
    fontWeight: 700,
    fontSize: "0.85rem",
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    textShadow: `0px 0px 10px ${getNeonColor(theme)}50`,
}));

const SectionIcon = styled(Box)(({ theme }) => ({
    color: getNeonColor(theme),
    display: "flex",
    alignItems: "center",
    "& svg": {
        fontSize: "1.1rem",
        filter: `drop-shadow(0px 0px 6px ${getNeonColor(theme)})`,
    },
}));

// Menu Item with hover effects
const MenuItem = styled(Box)(({ theme }) => ({
    padding: "10px 14px",
    marginBottom: "4px",
    borderRadius: "8px",
    cursor: "pointer",
    color: "rgba(255, 255, 255, 0.75)",
    fontSize: "0.9rem",
    fontWeight: 500,
    letterSpacing: "0.3px",
    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    overflow: "hidden",
    "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: "50%",
        transform: "translateY(-50%) scaleY(0)",
        width: "3px",
        height: "60%",
        background: getNeonColor(theme),
        borderRadius: "0 4px 4px 0",
        transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow: `0px 0px 8px ${getNeonColor(theme)}`,
    },
    "&:hover": {
        background: `linear-gradient(135deg, 
            ${getNeonColor(theme)}12 0%, 
            ${getNeonColor(theme)}20 50%, 
            ${getNeonColor(theme)}12 100%)`,
        color: getNeonColor(theme),
        paddingLeft: "20px",
        textShadow: `0px 0px 15px ${getNeonColor(theme)}60`,
        boxShadow: `
            0px 0px 20px ${getNeonColor(theme)}15,
            inset 0px 0px 15px ${getNeonColor(theme)}08
        `,
        "&::before": {
            transform: "translateY(-50%) scaleY(1)",
        },
    },
    "&:active": {
        transform: "scale(0.98)",
    },
}));

// Empty section placeholder
const EmptySection = styled(Typography)(({ theme }) => ({
    color: "rgba(255, 255, 255, 0.3)",
    fontSize: "0.8rem",
    fontStyle: "italic",
    padding: "10px 14px",
}));

interface HeaderProps {
    logout: () => void;
}

export default function Header(props: HeaderProps) {

    const context = useReferentialContext();
    const navigate = useNavigate();
    const location = useLocation();

    // Convert Login type to BaseUser for the TopMenu
    const user: BaseUser = {
        username: context.user.username,
        team: context.user.team,
        organization: context.user.organization,
        is_staff: context.user.is_staff,
        is_org_owner: context.user.is_org_owner,
        is_superuser: context.user.is_staff,
        email: context.user.email,
    };

    const navigateTo = (path: string, onClose?: () => void) => {
        navigate(path);
        onClose?.();
    };

    // Define menu items for the TopMenu component
    const menuItems = [
        {
            id: "competitive",
            label: "Competitive",
            icon: <EmojiEvents />,
            activeSectionMatcher: (pathname: string) => pathname.startsWith("/competitive"),
            menuContent: (onClose: () => void) => (
                <MenuContainer>
                    {/* Decorative background glow */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: "20%",
                            left: "10%",
                            width: "200px",
                            height: "200px",
                            background: (theme) => getNeonColor(theme),
                            opacity: 0.04,
                            filter: "blur(60px)",
                            borderRadius: "50%",
                            pointerEvents: "none",
                        }}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: "10%",
                            right: "15%",
                            width: "150px",
                            height: "150px",
                            background: (theme) => getNeonColor(theme),
                            opacity: 0.03,
                            filter: "blur(50px)",
                            borderRadius: "50%",
                            pointerEvents: "none",
                        }}
                    />
                    
                    <Stack direction="row" spacing={3} sx={{ position: "relative", zIndex: 1 }}>
                        {/* Map Section */}
                        <MenuSection>
                            <SectionHeader>
                                <SectionIcon><Map /></SectionIcon>
                                <SectionTitle>Map</SectionTitle>
                            </SectionHeader>
                            <MenuItem onClick={() => navigateTo("/competitive-game-review", onClose)}>
                                Game Review
                            </MenuItem>
                            <MenuItem onClick={() => navigateTo("/competitive-kills-heatmap", onClose)}>
                                Kills Heatmap
                            </MenuItem>
                            <MenuItem onClick={() => navigateTo("/competitive-smokes-heatmap", onClose)}>
                                Smokes Heatmap
                            </MenuItem>
                            <MenuItem onClick={() => navigateTo("/competitive-positions-heatmap", onClose)}>
                                Positions Heatmap
                            </MenuItem>
                        </MenuSection>

                        {/* Stats Section */}
                        <MenuSection>
                            <SectionHeader>
                                <SectionIcon><QueryStats /></SectionIcon>
                                <SectionTitle>Stats</SectionTitle>
                            </SectionHeader>
                            <MenuItem onClick={() => navigateTo("/competitive-clutches", onClose)}>
                                Clutches
                            </MenuItem>
                            <MenuItem onClick={() => navigateTo("/competitive-rounds", onClose)}>
                                Rounds
                            </MenuItem>
                            <MenuItem onClick={() => navigateTo("/competitive-players", onClose)}>
                                Players
                            </MenuItem>
                            <MenuItem onClick={() => navigateTo("/competitive-maps", onClose)}>
                                Maps
                            </MenuItem>
                        </MenuSection>

                        {/* Drafts Section */}
                        <MenuSection>
                            <SectionHeader>
                                <SectionIcon><AutoAwesome /></SectionIcon>
                                <SectionTitle>Drafts</SectionTitle>
                            </SectionHeader>
                            <MenuItem onClick={() => navigateTo("/competitive-drafts", onClose)}>
                                Drafts
                            </MenuItem>
                        </MenuSection>

                        {/* Storytelling Section */}
                        <MenuSection>
                            <SectionHeader>
                                <SectionIcon><Campaign /></SectionIcon>
                                <SectionTitle>Storytelling</SectionTitle>
                            </SectionHeader>
                            <EmptySection>Coming soon...</EmptySection>
                        </MenuSection>
                    </Stack>
                </MenuContainer>
            ),
        },
        {
            id: "scrims",
            label: "Scrims",
            icon: <SportsEsports />,
            activeSectionMatcher: (pathname: string) => pathname.startsWith("/scrims"),
            menuContent: (onClose: () => void) => (
                <MenuContainer sx={{ width: "240px" }}>
                    <Box
                        sx={{
                            position: "absolute",
                            top: "20%",
                            left: "10%",
                            width: "200px",
                            height: "200px",
                            background: (theme) => getNeonColor(theme),
                            opacity: 0.04,
                            filter: "blur(60px)",
                            borderRadius: "50%",
                            pointerEvents: "none",
                        }}
                    />

                    <Box sx={{ position: "relative", zIndex: 1 }}>
                        <MenuSection>
                            <SectionHeader>
                                <SectionIcon><QueryStats /></SectionIcon>
                                <SectionTitle>Reviewing</SectionTitle>
                            </SectionHeader>
                            <MenuItem onClick={() => navigateTo("/scrims-history", onClose)}>
                                History
                            </MenuItem>
                        </MenuSection>
                    </Box>
                </MenuContainer>
            ),
        },
    ];

    return (
        <TopMenu
            logout={props.logout}
            user={user}
            menuItems={menuItems}
            teamIconComponent={TeamIcon}
            routes={{
                profile: "/profile",
                manageOrg: "/management-organizations",
            }}
            dependencies={{
                NeonAppBar: NeonAppBar,
                getNeonColor: getNeonColor,
            }}
        />
    );
}
